<template>
  <v-menu
    v-model="menuOpen"
    offset-y
    left
    nudge-bottom="14"
    min-width="230"
    content-class="user-profile-menu-content"
    @input="onMenuChange"
  >
    <selecionar-pausa-dialog :is-pausa-dialog-open.sync="isPausaDialogOpen" />

    <template v-slot:activator="{ on, attrs }">
      <div
        v-bind="attrs"
        class="d-flex align-center right-row"
        v-on="on"
        color="primary"
      >
        <v-badge
          bottom
          color="success"

          offset-x="12"
          offset-y="12"
          class="ms-4 my-2"
          dot
        >
          <v-avatar
            size="40px"
            color="primary"
            class="v-avatar-light-bg primary--text"
          >
            <v-img
              v-if="isLoggedIn && usuarioAvatar"
              :src="usuarioAvatar"
            ></v-img>
            <v-icon
              v-else
              color="primary"
              size="28"
            >
              {{ icons.mdiAccountOutline }}
            </v-icon>
          </v-avatar>
        </v-badge>
        <span
          v-if="!$vuetify.breakpoint.mdAndDown"
          class="text--primary font-weight-semibold mb-n1 ml-5"
        >{{ usuarioNome }}</span>
      </div>
    </template>
    <v-list>
      <!-- Settings -->
      <v-list-item :to="{ name: 'conta' }">
        <v-list-item-icon class="me-2">
          <v-icon size="22">
            {{ icons.mdiCogOutline }}
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>Conta</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider
        class="my-2"
      ></v-divider>

      <!-- FAQ -->
      <v-list-item
        :to="{ name: 'faq' }"
        target="_blank"
      >
        <v-list-item-icon class="me-2">
          <v-icon size="22">
            {{ icons.mdiHelpCircleOutline }}
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>FAQ</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider class="my-2"></v-divider>

      <!-- Theme -->
      <v-list-item @click="isDark = !isDark">
        <v-list-item-icon class="me-2">
          <v-icon>
            {{ isDark ? icons.mdiWeatherSunny : icons.mdiWeatherNight }}
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>Tema</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider class="my-2"></v-divider>

      <v-list-item @click.stop="openPausaDialog">
        <v-list-item-icon class="me-2">
          <v-icon>
            {{ icons.mdiStopCircleOutline }}
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>Pausa</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider class="my-2"></v-divider>

      <!-- Logout -->
      <v-list-item @click="logoutUser">
        <v-list-item-icon class="me-2">
          <v-icon size="22">
            {{ icons.mdiLogoutVariant }}
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>Sair</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import {
  mdiAccountOutline,
  mdiCogOutline,
  mdiHelpCircleOutline,
  mdiLogoutVariant,
  mdiWeatherSunny,
  mdiWeatherNight,
  mdiStopCircleOutline,
} from '@mdi/js'
import useAppConfig from '@core/@app-config/useAppConfig'
import { ref, watch } from '@vue/composition-api'
import { storeToRefs } from 'pinia'
import SelecionarPausaDialog from '@/views/pausas/SelecionarPausaDialog.vue'
import { useRouter } from '@/@core/utils'
import AuthService from '@/services/modules/AuthService'
import useUserStore from '@/store/user'
import usePausaStore from '@/store/pausas'
import { EventBus } from '@/utils/EventBus'

export default {
  components: {
    SelecionarPausaDialog,
  },

  setup() {
    const {
      nome: usuarioNome,
      avatar: usuarioAvatar,
      isLoggedIn,
    } = storeToRefs(useUserStore())

    const { route } = useRouter()
    const menuOpen = ref(false)
    const onMenuChange = value => {
      menuOpen.value = value
    }

    watch(
      () => route.value.fullPath,
      () => {
        menuOpen.value = false
      },
    )
    const { isDark } = useAppConfig()

    async function logoutAgente() {
      await AuthService.logoutAgente()
    }

    async function logoutUser() {
      try {
        await logoutAgente()
      } finally {
        EventBus.$emit('logged-out')
      }
    }

    const { id: pausaId, bloquearTela: pausaBloquearTela } = storeToRefs(usePausaStore())
    const isPausaDialogOpen = ref(false)

    /*
     * Watch de () => pausaId.value, não () => pausaId: o ref em si nunca muda de referência, então
     * observar o ref direto (como estava antes) só disparava uma vez, no immediate, e nunca mais
     * reagia a troca de valor -- por exemplo quando usePausaValidation (App.vue) zera o store
     * porque a pausa persistida no localStorage não existe mais no backend.
     */
    watch(
      () => pausaId.value,
      v => {
        isPausaDialogOpen.value = !!v && pausaBloquearTela.value
      },
      { immediate: true },
    )

    function openPausaDialog() {
      isPausaDialogOpen.value = true
    }

    return {
      isLoggedIn,
      usuarioNome,
      usuarioAvatar,
      menuOpen,
      onMenuChange,
      isPausaDialogOpen,
      openPausaDialog,
      isDark,
      logoutUser,
      icons: {
        mdiAccountOutline,
        mdiCogOutline,
        mdiHelpCircleOutline,
        mdiLogoutVariant,
        mdiWeatherSunny,
        mdiWeatherNight,
        mdiStopCircleOutline,
      },
    }
  },
}
</script>

<style lang="scss">
.user-profile-menu-content {
  .v-list-item {
    min-height: 2.5rem !important;
  }

  // --- LÓGICA DE IDENTIDADE VISUAL (SÓ PARA TEMA LIGHT) ---
  // O uso de @at-root ou colocar fora do aninhamento garante que pegue a classe global
  .v-application.theme--light & {
    background-color: #8E117B !important; // Fundo Verde da Empresa
    border: 1px solid #8E117B !important; // Remove bordas brancas se houver

    // Força Ícones e Textos para Preto (Contraste)
    .v-list-item__title,
    .v-icon {
      color: #000000 !important;
    }

    // Hover Personalizado (Branco semi-transparente fica bonito no neon)
    .v-list-item:hover {
      background-color: rgba(255, 255, 255, 0.3) !important;
    }
  }
  // --------------------------------------------------------

  // Hover Padrão (Fallback para Dark Mode)
  .v-application.theme--dark & {
    .v-list-item:hover {
      background-color: rgba(255, 255, 255, 0.08) !important;
    }
  }
}
</style>
